import { Card, IconButton, LuIcon, Text } from "@overmap-ai/blocks"
import { UNTITLED_ISSUE_TITLE } from "@overmap-ai/common"
import { selectIssueById } from "@overmap-ai/core"
import { useNavigate } from "@tanstack/react-router"
import { type NodeViewProps, NodeViewWrapper } from "@tiptap/react"
import { cx } from "class-variance-authority"
import { memo, type MouseEventHandler, useCallback } from "react"

import { IssueTypeStatusIcon } from "../../../components/Icons/IssueTypeStatusIcon"
import { LuIconWithWrapper } from "../../../components/Icons/LuIconWithWrapper"
import { IssueIndexLabel } from "../../../components/IndexLabels/IssueIndex"
import { modelIconMapping } from "../../../mappings/icons"
import { useAppSelector } from "../../../store"

export const IssueLinkWrapper = memo((props: NodeViewProps) => {
	const { node, deleteNode, selected, editor } = props
	const issueId = node.attrs.uuid as string

	const issue = useAppSelector(selectIssueById(issueId))
	const navigate = useNavigate()

	const handleClick = useCallback(() => {
		if (!issue) return
		void navigate({
			from: "/$organizationId/$projectId",
			to: "/$organizationId/$projectId/issues/$issueId",
			params: (prev) => ({
				...prev,
				issueId: issue.uuid,
			}),
		})
	}, [issue, navigate])

	const handleDeleteButtonClicked = useCallback<MouseEventHandler>(
		(e) => {
			e.stopPropagation()
			deleteNode()
		},
		[deleteNode],
	)

	return (
		<NodeViewWrapper className="my-4">
			{issue ? (
				<Card
					className={cx("flex cursor-pointer items-center justify-between gap-2", {
						"outline-(--primary-indicator) outline-2 -outline-offset-1":
							selected && editor.isEditable && editor.isFocused,
					})}
					onClick={handleClick}
					size="sm"
				>
					<div className="flex min-w-0 grow items-center gap-2">
						<IssueIndexLabel
							issueId={issue.uuid}
							className="shirnk-0 text-(--base-a11) whitespace-nowrap"
						/>
						<IssueTypeStatusIcon issueTypeStatusId={issue.status} background={false} />
						<Text className="truncate font-medium">{issue.title || UNTITLED_ISSUE_TITLE}</Text>
					</div>

					{editor.isEditable && (
						<IconButton
							aria-label="delete"
							accentColor="base"
							variant="ghost"
							size="sm"
							onClick={handleDeleteButtonClicked}
						>
							<LuIcon icon={modelIconMapping.close} />
						</IconButton>
					)}
				</Card>
			) : (
				<Card
					className={cx("flex cursor-pointer items-center justify-between gap-2", {
						"outline-(--primary-indicator) outline-2 -outline-offset-1": selected && editor.isEditable,
					})}
					size="sm"
				>
					<div className="flex items-center gap-2">
						<LuIconWithWrapper icon={modelIconMapping.issue} background />
						<Text accentColor="base">Issue not found</Text>
					</div>

					{editor.isEditable && (
						<IconButton
							aria-label="delete"
							accentColor="base"
							size="sm"
							variant="ghost"
							onClick={handleDeleteButtonClicked}
						>
							<LuIcon icon={modelIconMapping.close} />
						</IconButton>
					)}
				</Card>
			)}
		</NodeViewWrapper>
	)
})
IssueLinkWrapper.displayName = "IssueLinkWrapper"
